import {useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {useCreateUploadUrl} from '../../../logic'
import {maxImageBytes, resizableTypeCheck, shrinkFile} from '../../shared/useFileUpload'

// Mirrors the extensions allowed by the document-upload flow in useFileUpload.tsx
export const ALLOWED_ATTACHMENT_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'pdf']
export const ATTACHMENT_ACCEPT = ALLOWED_ATTACHMENT_EXTENSIONS.map(ext => `.${ext}`).join()

export type AttachmentState =
  | { status: 'idle' }
  | { status: 'uploading'; file: File }
  | { status: 'ready'; file: File; documentFileName: string; documentId: string; originalFileName: string }
  | { status: 'error'; file: File; message: string }

// Invisible helper that requests a presigned S3 upload URL for `file` and PUTs it once
// the URL is ready, mirroring the presigned-upload flow in useFileUpload.tsx's UploadItem.
export function ReplyAttachmentUploader({ file, companyIdOrderIdHash, onUploaded, onError }: {
  file: File;
  companyIdOrderIdHash: string;
  onUploaded: (result: { documentFileName: string; documentId: string; originalFileName: string }) => void;
  onError: (message: string) => void;
}) {
  const { search } = useLocation()
  const FileExtension = file.name.split('.').reverse()[0]
  const {data, error} = useCreateUploadUrl({
    CompanyIdOrderIdHash: companyIdOrderIdHash,
    FileName: file.name,
    FileExtension,
    UploadType: 'reply',
    search,
  })
  const [uploadStarted, setUploadStarted] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)

  useEffect(() => {
    let cancelled = false

    const isImageType = file.type.includes(resizableTypeCheck)
    const isImageTooLarge = file.size > maxImageBytes

    if (isImageType && isImageTooLarge) {
      shrinkFile(file)
        .then(shrunk => {
          if (!cancelled) setUploadFile(shrunk)
        })
        .catch(() => {
          if (!cancelled) setUploadFile(file)
        })
    } else {
      setUploadFile(file)
    }

    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let cancelled = false

    if (error) {
      onError('Error getting an upload URL for this file.')
      return
    }

    if (data?.Url && uploadFile && !uploadStarted) {
      setUploadStarted(true)

      fetch(data.Url, {
        method: 'PUT',
        body: uploadFile,
        headers: {
          'x-amz-server-side-encryption': 'AES256',
          'x-amz-acl': 'bucket-owner-full-control',
        },
      }).then(res => {
        if (!res.ok) throw new Error(`Upload failed with status ${res.status}`)
        if (!cancelled) onUploaded({ documentFileName: data.DocumentFileName, documentId: data.DocumentId, originalFileName: file.name })
      }).catch(() => {
        if (!cancelled) onError('Error uploading this file.')
      })
    }

    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, uploadFile])

  return null
}
